import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * SessionStore mirrors the Auth0 state we need in synchronous code paths.
 * The Auth0 SDK exposes async `getAccessTokenSilently()` which is awkward
 * to call from inside route transitions — so we cache the most recent
 * token here and refresh it in `<ApiAuthBridge />` on every Auth0 state
 * change.
 *
 * Persistence is limited to `accountBootstrapped` so we can skip a
 * round-trip to `/auth/callback` on quick reloads; the token itself is
 * never persisted (Auth0 handles token storage via the SDK).
 */
export interface SessionState {
  accessToken: string | null;
  accountBootstrapped: boolean;
  setAccessToken: (token: string | null) => void;
  setAccountBootstrapped: (bootstrapped: boolean) => void;
  reset: () => void;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      accessToken: null,
      accountBootstrapped: false,
      setAccessToken: (accessToken) => set({ accessToken }),
      setAccountBootstrapped: (accountBootstrapped) => set({ accountBootstrapped }),
      reset: () => set({ accessToken: null, accountBootstrapped: false }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ accountBootstrapped: state.accountBootstrapped }),
    },
  ),
);
